import i18n from '@shared/config/i18n';
import { ChildBase, ValuesSpec } from '../defaults';
import { Token } from './addButton';

const { t } = i18n;

type ItemData = {
  id: number;
  name: string;
  status: string;
  url: string | null;
  file: string;
  total: number;
};

type Item = {
  id: string;
  minInvocePrice: number;
  maxInvocePrice: number;
  invocesToken: string;
  horisontalInvocesToken: string;
  paymentTypesList: ValuesSpec[];
  domenUploadFile: string;
  token: Token;
  data?: ItemData;
  readOnly: boolean;
  rootToken: string;
  deleteButtonIcon: boolean;
  isStatusesSelect?: boolean;
  valid: boolean;
  canChangeReadonly: boolean;
  deleteInvocesToken: string;
};

/* eslint-disable */
export const item = ({
  id,
  minInvocePrice,
  maxInvocePrice,
  invocesToken,
  horisontalInvocesToken,
  paymentTypesList,
  domenUploadFile,
  token,
  data,
  readOnly,
  rootToken,
  deleteButtonIcon,
  isStatusesSelect,
  valid,
  canChangeReadonly,
  deleteInvocesToken,
}: Item) => {
  const mainField: ChildBase =
    token === 'add-link'
      ? {
          spec: 'input-text',
          token: `${id}_link`,
          data: {
            label: t('Link'),
            placeholder: t('EnterLinkText'),
            value: data?.url ?? '',
            type: 'text',
            multiline: true,
            readOnly,
            customData: {
              clearable: true,
              text: {
                readOnlyAsLink: true,
              },
            },
            valid,
            canChangeReadonly,
            mandatory: true,
          },
          styles: { wordBreak: 'break-all' },
          meta: {
            validations: [
              {
                expr: {
                  type: 'func',
                  value: `({hashMap}) => !!hashMap['${id}_link'].data.value?.length`,
                },
                message: t('thisIsRequiredField'),
                level: 'error',
              },
            ],
          },
        }
      : {
          spec: 'input-upload-file',
          token: `${id}_file`,
          data: {
            label: t('File'),
            pending: 0,
            readOnly,
            canChangeReadonly,
            value: !!data?.file
              ? [
                  {
                    id: data.id,
                    name: data.name,
                    bytes: 0,
                    url: data.file,
                  },
                ]
              : [],
            multiple: false,
            mandatory: true,
            customData: {
              uploadFile: {
                addButton: t('Attach'),
                uploadFileAccept: ['image/png', 'image/jpeg', 'application/pdf'],
                uploadFileAcceptTypes: ['png', 'jpg', 'jpeg', 'pdf'],
                maxSizeOfFile: 1000000,
              },
            },
            valid,
          },
          child: [],
          meta: {
            validations: [
              {
                expr: {
                  type: 'func',
                  value: `({hashMap}) => !!hashMap['${id}_file'].data.value?.length`,
                },
                message: t('thisIsRequiredField'),
                level: 'error',
              },
            ],
            triggers: [
              {
                events: ['on-input-file-change'],
                targets: [`${id}_file`],
                handlers: ['show-file-pending-loader', 'upload-file'],
              },
              {
                events: ['data-fetch-error'],
                targets: [`${id}_file`],
                handlers: ['hide-file-pending-loader', 'show-modal-error'],
                expr: {
                  type: 'func',
                  value: `({sourceAction}) => sourceAction.token === 'upload-file'`,
                },
              },
              {
                events: ['data-fetch-success'],
                targets: [`${id}_file`],
                handlers: ['hide-file-pending-loader', 'add-file-to-uploaded'],
                expr: {
                  type: 'func',
                  value: `({sourceAction}) => sourceAction.token === 'upload-file'`,
                },
              },
            ],
            actions: [
              {
                kind: 'changes',
                token: 'show-file-pending-loader',
                spec: {
                  target: `${id}_file`,
                  changes: {
                    'data.pending': {
                      type: 'func',
                      value: `({hashMap}) => hashMap['${id}_file'].data.pending + 1`,
                    },
                  },
                },
              },
              {
                kind: 'changes',
                token: 'hide-file-pending-loader',
                spec: {
                  target: `${id}_file`,
                  changes: {
                    'data.pending': {
                      type: 'func',
                      value: `({hashMap}) => hashMap['${id}_file'].data.pending - 1`,
                    },
                  },
                },
              },
              {
                kind: 'changes',
                token: 'add-file-to-uploaded',
                spec: {
                  target: `${id}_file`,
                  changes: {
                    'data.value': {
                      type: 'func',
                      value: `({responseRequestData, hashMap}) => {
                        const requestData = responseRequestData.request.data.file

                        return [{
                          id: Number(responseRequestData.response.data.id),
                          name: requestData.name,
                          bytes: requestData.size,
                        }]
                      }`,
                    },
                  },
                },
              },
              {
                kind: 'api',
                token: 'upload-file',
                spec: {
                  uri: `${domenUploadFile}/uploadFile`,
                  method: 'POST',
                  headers: { 'Content-Type': 'multipart/form-data' },
                  data: {
                    app: {
                      type: 'func',
                      value: '({config}) => config.custom.app',
                    },
                    token: {
                      type: 'func',
                      value: '({config}) => config.custom.token',
                    },
                    timezone: {
                      type: 'func',
                      value: '({config}) => config.custom.timezone',
                    },
                    file: {
                      type: 'func',
                      value: `({event}) => event.file`,
                    },
                  },
                },
              },
            ],
          },
        };

  const status = {
    spec: 'input-select',
    token: `${id}_status`,
    styles: { flex: '1 1 30%', minWidth: '220px' },
    data: {
      label: t('Label.status'),
      placeholder: t('selectStatuse'),
      value: data?.status ?? null,
      readOnly,
      valid,
      values: {
        kind: 'const',
        spec: paymentTypesList,
      },
      mandatory: true,
      customData: {
        select: {
          notMultipleReadonlyAsChip: true,
        },
      },
    },
    child: [],
    meta: {
      validations: [
        {
          expr: {
            type: 'func',
            value: `({hashMap}) => hashMap['${id}_status'].data.value !== null`,
          },
          message: t('thisIsRequiredField'),
          level: 'error',
        },
      ],
    },
  };

  const statusData = [];
  if (isStatusesSelect) statusData.push(status);

  return {
    spec: 'controls-horizontal',
    token: `${id}_horizontal-new-item`,
    data: {},
    styles: {
      display: 'flex',
      gap: 8,
      width: '100%',
      boxSizing: 'border-box',
    },
    child: [
      {
        spec: 'input-text',
        token: `${id}_id`,
        data: {
          value: data?.id ?? 'new',
          type: 'text',
          visible: false,
        },
        child: [],
      },

      mainField,

      {
        spec: 'input-text',
        token: `${id}_price`,
        styles: { flex: '1 1 30%', minWidth: '120px' },
        data: {
          label: t('AmountPayable'),
          placeholder: t('EnterAmount'),
          value: data?.total ?? '',
          type: 'number',
          mandatory: true,
          customData: {
            clearable: true,
            sxMui: {
              '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                '-webkit-appearance': 'none',
                margin: 0,
              },
              '& input[type=number]': { '-moz-appearance': 'textfield' },
            },
          },
          readOnly,
          valid,
        },
        meta: {
          validations: [
            {
              expr: {
                type: 'func',
                value: `({hashMap}) => hashMap['${id}_price'].data.value >= ${minInvocePrice} 
&& hashMap['${id}_price'].data.value <= ${maxInvocePrice}`,
              },
              message: `${t('EnterAmountFrom')} ${minInvocePrice} ${t('to')} ${maxInvocePrice}`,
              level: 'error',
            },
          ],
        },
      },

      ...statusData,

      {
        spec: 'button-icon',
        token: `${id}_delete-button-icon`,
        data: {
          visible: deleteButtonIcon,
          customData: {
            buttonIcon: {
              iconMui: 'DeleteForever',
              colorMui: 'error',
              title: t('DeleteInvoice'),
            },
          },
        },
        meta: {
          triggers: [
            {
              events: ['on-click'],
              targets: [`${id}_delete-button-icon`],
              handlers: [`${id}_add-to-deleted`, `${id}_delete-button-handler`],
            },
          ],
          actions: [
            {
              kind: 'func',
              token: `${id}_delete-button-handler`,
              spec: {
                func: `({hashMap, source, setSduiSource}) => {
  const newInvoces = source.child.find(({token}) => token === '${rootToken}').child.find(({token}) => token === '${invocesToken}').child.find(({token}) => token === '${horisontalInvocesToken}').child
  const deleteItemIndex = newInvoces.findIndex(({token}) => token.startsWith('${id}_'))

  newInvoces.splice(deleteItemIndex, 1)
  setSduiSource({source, add: true})
}`,
              },
            },

            {
              kind: 'changes',
              token: `${id}_add-to-deleted`,
              spec: {
                target: deleteInvocesToken,
                changes: {
                  'data.value': {
                    type: 'func',
                    value: `({hashMap}) => {
  const deleteInvoceIds = hashMap['${deleteInvocesToken}'].data.value
  if (!'${id}'.startsWith('new')) deleteInvoceIds.push(${id})
  return deleteInvoceIds
}`,
                  },
                },
              },
            },
          ],
        },
      },
    ],
  } as ChildBase;
};
/* eslint-enable */
